import { ArrowRight, Camera, CheckCircle, Clock, Pill, Star } from "lucide-react";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Reveal } from "@/components/reveal";
import SEO from "@/components/seo";

const BOOKING_URL =
  "https://www.timetopet.com/portal/tallylassiepets/create-account";

const benefits = [
  {
    icon: CheckCircle,
    title: "Care that fits the visit",
    description:
      "Combine a walk, feeding, a short let-out, or a home check to fit your pet's needs.",
  },
  {
    icon: Camera,
    title: "Photos and visit reports",
    description:
      "Get photos and a comprehensive report after your pet's visit.",
  },
  {
    icon: Pill,
    title: "Medication included",
    description:
      "Medication administration is included at no additional charge.",
  },
];

export default function PetPalVisits() {
  return (
    <div>
      <SEO
        title="Pet Pal Visits | Dog Walking & Pet Sitting"
        description="Book a 30-minute Pet Pal Visit with Tallahassee Lassie. Combine a walk, feeding, let-out, or home check, with photos and a visit report."
        path="/pet-pal-visits"
      />

      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="mx-auto grid max-w-6xl items-center gap-10 md:grid-cols-2 md:gap-14">
            <Reveal>
              <div>
                <div className="mb-6 inline-flex items-center gap-2 rounded-full bg-secondary/10 px-4 py-2 text-sm font-semibold text-secondary">
                  <Star size={16} fill="currentColor" />
                  Our most popular visit
                </div>
                <h1 className="mb-6 font-serif text-4xl font-bold leading-tight text-foreground md:text-5xl lg:text-6xl">
                  Pet Pal Visits
                </h1>
                <p className="mb-8 max-w-xl text-lg leading-relaxed text-muted-foreground md:text-xl">
                  One-on-one care for pets who need a walk, a meal, a quick
                  let-out, or a little extra attention while you&apos;re away.
                </p>
                <div className="mb-8 flex flex-wrap gap-x-6 gap-y-3 text-sm font-semibold text-foreground/75">
                  <span className="inline-flex items-center gap-2">
                    <Clock size={17} className="text-primary" />
                    30-minute visit
                  </span>
                  <span>Starts at $35</span>
                </div>
                <div className="flex flex-col gap-3 sm:flex-row">
                  <Button asChild size="lg" className="h-14 px-7">
                    <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                      Create an Account
                    </a>
                  </Button>
                  <Button
                    asChild
                    size="lg"
                    variant="outline"
                    className="h-14 border-primary px-7 text-primary hover:bg-primary hover:text-white"
                  >
                    <Link href="/menu">Explore all services</Link>
                  </Button>
                </div>
              </div>
            </Reveal>

            <Reveal delay={150}>
              <img
                src="https://images.squarespace-cdn.com/content/v1/61a9415d512d9d24f6cc2d59/1704215622943-1KXSQCE1QNTBWBVRME5Y/image-asset.jpeg"
                alt="A dog enjoying a one-on-one Pet Pal Visit"
                className="aspect-[4/3] w-full object-cover shadow-xl"
                style={{ borderRadius: "32px 0 32px 0" }}
                fetchPriority="high"
              />
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-muted/30 py-16 md:py-24">
        <div className="container mx-auto px-4">
          <Reveal>
            <div className="mx-auto mb-12 max-w-3xl text-center">
              <h2 className="mb-4 font-serif text-3xl font-bold text-foreground md:text-4xl">
                A visit shaped around your pet
              </h2>
              <p className="text-lg leading-relaxed text-muted-foreground">
                Choose the care your pet needs that day, with an update waiting
                for you when the visit is complete.
              </p>
            </div>
          </Reveal>

          <div className="mx-auto grid max-w-5xl gap-5 md:grid-cols-3">
            {benefits.map((benefit, index) => (
              <Reveal key={benefit.title} delay={index * 70}>
                <article
                  className="h-full border border-border/60 bg-card p-7"
                  style={{ borderRadius: "20px 0 20px 0" }}
                >
                  <span className="mb-5 inline-flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
                    <benefit.icon size={22} />
                  </span>
                  <h3 className="mb-2 font-serif text-xl font-bold text-foreground">
                    {benefit.title}
                  </h3>
                  <p className="leading-relaxed text-foreground/75">
                    {benefit.description}
                  </p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <Reveal>
            <div
              className="mx-auto flex max-w-5xl flex-col gap-6 bg-primary p-8 text-primary-foreground md:flex-row md:items-center md:justify-between md:p-12"
              style={{ borderRadius: "32px 0 32px 0" }}
            >
              <div className="max-w-2xl">
                <h2 className="mb-3 font-serif text-3xl font-bold">
                  Need a longer visit or overnight care?
                </h2>
                <p className="text-lg leading-relaxed text-primary-foreground/85">
                  See the other in-home visit options, including Longer Love
                  and Bed &amp; Breakfast.
                </p>
              </div>
              <Button
                asChild
                size="lg"
                variant="secondary"
                className="h-14 shrink-0 bg-white px-7 text-primary hover:bg-white/90"
              >
                <Link href="/pet-sitting">
                  Explore pet sitting <ArrowRight size={17} className="ml-2" />
                </Link>
              </Button>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
